import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, model, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Attendance, CalendarAppointmentDto, DocumentationPopulatedResponseDto } from '@pflegix/types';
import {
  AppIconEntity,
  AppIconService,
  IconWithTextComponent,
  PanelComponent,
  SkeletonLoadingComponent,
  ToastService,
} from '@pflegix/ui';
import { addMonths, endOfMonth } from 'date-fns';
import { lastValueFrom } from 'rxjs';
import { CalendarAppointmentApi } from '../../appointment-management';
import { getCareLevelFromBusinessPartner } from '../../business-partner-management';
import { BusinessPartnerStore } from '../../business-partner-management/store';
import { DocumentationApi } from '../api';

@Component({
  selector: 'pfx-documentation-care-consultation-stats',
  imports: [RouterModule, PanelComponent, SkeletonLoadingComponent, IconWithTextComponent, DatePipe],
  templateUrl: './documentation-care-consultation-stats.component.html',
  styles: '',
})
export class DocumentationCareConsultationStatsComponent implements OnInit {
  public businessPartnerId = input.required<string>();

  protected consultationFilter = computed(() => {
    const businessPartnerId = this.businessPartnerId();

    if (!businessPartnerId) return '';
    return (
      `partners.dependent$eq$${businessPartnerId},` +
      `performedServices.attendances$eq$${Attendance.NV_282_CONSULTATION_VISIT}`
    );
  });

  protected careLevel = computed(() =>
    getCareLevelFromBusinessPartner(this.businessPartnerStore.entityMap()[this.businessPartnerId()])
  );

  protected nextConsultation = computed(() => {
    const consultations = this.consultationCalendarAppointments();

    if (!consultations || consultations.length === 0) return undefined;

    return consultations[0].startAt;
  });

  protected lastConsultation = computed(() => {
    const consultations = this.consultationDocumentations();

    if (!consultations || consultations.length === 0) return undefined;

    return consultations[0].period.startAt;
  });

  protected lastConsultationExpiryDate = computed(() => {
    const lastConsultation = this.lastConsultation();

    if (!lastConsultation) return undefined;

    if (this.careLevel() === '1') {
      return addMonths(lastConsultation, 12);
    }

    if (this.careLevel() === '2' || this.careLevel() === '3') {
      return endOfMonth(addMonths(lastConsultation, 6));
    }
    if (this.careLevel() === '4' || this.careLevel() === '5') {
      return endOfMonth(addMonths(lastConsultation, 3));
    }

    return undefined;
  });

  private documentationApi = inject(DocumentationApi);

  private calendarAppointmentApi = inject(CalendarAppointmentApi);

  private toastService = inject(ToastService);

  private appIconService = inject(AppIconService);

  private businessPartnerStore = inject(BusinessPartnerStore);

  public isLoading = model(true);

  protected consultationDocumentations = model<DocumentationPopulatedResponseDto[] | undefined>(undefined);

  protected consultationCalendarAppointments = model<CalendarAppointmentDto[] | undefined>(undefined);

  protected icons = {
    signature: this.appIconService.getAppIcon(AppIconEntity.DOCUMENTATION).icon,
  };

  public ngOnInit(): void {
    this.getCaregiversOfDependentWithDocumentationCount();
    this.getCalendarAppointmentsOfDependentWithConsultation();
  }

  public async getCaregiversOfDependentWithDocumentationCount(): Promise<void> {
    this.isLoading.set(true);
    try {
      const consultations = await lastValueFrom(
        this.documentationApi.getDocumentationsPaginated(1, 100, '-createdAt', this.consultationFilter())
      );

      this.consultationDocumentations.set(consultations.docs);
    } catch (error) {
      console.error(error);
      this.toastService.addErrorToast('Fehler beim Laden der Pflegeberatungskalender');
    } finally {
      this.isLoading.set(false);
    }
  }

  public async getCalendarAppointmentsOfDependentWithConsultation(): Promise<void> {
    this.isLoading.set(true);
    try {
      const calendarAppointments = await lastValueFrom(
        this.calendarAppointmentApi.getAppointmentConsultationsByDependentId(
          this.businessPartnerId(),
          { page: 1, size: 100, sort: '-createdAt', filter: this.consultationFilter() },
          [Attendance.NV_282_CONSULTATION_VISIT]
        )
      );

      this.consultationCalendarAppointments.set(calendarAppointments.docs);
    } catch (error) {
      console.error(error);
      this.toastService.addErrorToast('Fehler beim Laden der Pflegeberatungskalender');
    } finally {
      this.isLoading.set(false);
    }
  }
}
